import { useState } from "react";
import { motion } from "framer-motion";
import { sendContact } from "../../lib/api";
import { useLang } from "../../i18n/LanguageContext";
import { toast } from "sonner";
import { Send, Loader2, Terminal } from "lucide-react";

const Field = ({ label, textarea, ...props }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-blue-300/70">{label}</span>
    {textarea ? (
      <textarea
        {...props}
        rows={4}
        className="mt-3 w-full resize-none border-0 border-b border-blue-500/25 bg-transparent pb-3 font-body text-white outline-none transition-colors duration-300 placeholder:text-[#405060] focus:border-blue-400"
      />
    ) : (
      <input
        {...props}
        className="mt-3 w-full border-0 border-b border-blue-500/25 bg-transparent pb-3 font-body text-white outline-none transition-colors duration-300 placeholder:text-[#405060] focus:border-blue-400"
      />
    )}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { t } = useLang();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.allRequired"));
      return;
    }
    try {
      setLoading(true);
      await sendContact(form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch (e) {
      toast.error(t("contact.fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">[ {t("contact.tag")} ]</span>
            <h2 className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl">
              {t("contact.title")}
            </h2>
            <p className="mt-6 max-w-md font-body text-base leading-relaxed text-[#8AA0C0]">
              {t("contact.subtitle")}
            </p>
            <div className="mt-10 flex items-center gap-3 rounded-full border border-blue-500/20 bg-blue-400/5 px-4 py-2.5">
              <Terminal size={14} className="text-blue-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-200">
                {t("contact.channel")}
              </span>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-8 backdrop-blur-xl lg:p-10"
          >
            <div className="space-y-8">
              <Field
                label={t("contact.name")}
                data-testid="contact-name"
                placeholder={t("contact.namePh")}
                value={form.name}
                onChange={set("name")}
              />
              <Field
                label={t("contact.email")}
                data-testid="contact-email"
                type="email"
                placeholder={t("contact.emailPh")}
                value={form.email}
                onChange={set("email")}
              />
              <Field
                label={t("contact.message")}
                textarea
                data-testid="contact-message"
                placeholder={t("contact.messagePh")}
                value={form.message}
                onChange={set("message")}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group mt-10 inline-flex w-full items-center justify-center gap-3 rounded-full bg-blue-400 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#0F172A] transition-all duration-300 hover:glow-cyan disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {loading ? t("contact.submitting") : t("contact.submit")}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
